import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEmployee } from "@/hooks/useEmployee";
import { usePerformanceMetrics } from "@/hooks/usePerformanceMetrics";
import { usePerformanceGoals } from "@/hooks/usePerformanceGoals";
import { usePerformanceIncentives } from "@/hooks/usePerformanceIncentives";
import { TrendingUp, Target, Award, Star } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

export default function EmployeePerformanceDashboard() {
  const { data: currentEmployee, isLoading: employeeLoading } = useEmployee();
  const { data: metrics } = usePerformanceMetrics(currentEmployee?.id);
  const { data: goals } = usePerformanceGoals(currentEmployee?.id, "active");
  const { data: incentives } = usePerformanceIncentives(currentEmployee?.id);

  if (employeeLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">جاري التحميل...</div>
      </div>
    );
  }

  const latestMetric = metrics?.[0];
  const activeGoals = goals?.filter(g => g.status === 'active') || [];
  const completedGoals = goals?.filter(g => g.status === 'completed') || [];
  const pendingIncentives = incentives?.filter(i => i.status === 'pending') || [];

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'excellent': return 'text-green-600';
      case 'very_good': return 'text-blue-600';
      case 'good': return 'text-indigo-600';
      case 'average': return 'text-yellow-600';
      default: return 'text-red-600';
    }
  };

  const getGradeLabel = (grade: string) => {
    const labels: Record<string, string> = {
      excellent: 'ممتاز',
      very_good: 'جيد جداً',
      good: 'جيد',
      average: 'متوسط',
      needs_improvement: 'يحتاج تحسين'
    };
    return labels[grade] || grade;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">لوحة الأداء الشخصية</h1>
        <Badge variant="outline" className="text-lg">
          {currentEmployee?.name}
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">التقييم الشامل</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getGradeColor(latestMetric?.performance_grade || '')}`}>
              {latestMetric?.overall_score || 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              {getGradeLabel(latestMetric?.performance_grade || '')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">الأهداف النشطة</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeGoals.length}</div>
            <p className="text-xs text-muted-foreground">
              {completedGoals.length} مكتمل
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">الإيرادات</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {latestMetric?.total_revenue?.toLocaleString() || 0} ر.س
            </div>
            <p className="text-xs text-muted-foreground">
              {latestMetric?.total_bookings || 0} حجز
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">الحوافز</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {pendingIncentives.reduce((sum, i) => sum + Number(i.amount), 0).toLocaleString()} ر.س
            </div>
            <p className="text-xs text-muted-foreground">قيد الموافقة</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="metrics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="metrics">المقاييس</TabsTrigger>
          <TabsTrigger value="goals">الأهداف</TabsTrigger>
          <TabsTrigger value="incentives">الحوافز</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>مقاييس المبيعات</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">معدل التحويل</span>
                    <span className="font-bold">{latestMetric?.conversion_rate || 0}%</span>
                  </div>
                  <Progress value={latestMetric?.conversion_rate || 0} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">متوسط قيمة الصفقة</span>
                    <span className="font-bold">{latestMetric?.average_deal_size?.toLocaleString() || 0} ر.س</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>مقاييس خدمة العملاء</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">رضا العملاء</span>
                    <span className="font-bold">{latestMetric?.client_satisfaction_score || 0}/5</span>
                  </div>
                  <Progress value={(latestMetric?.client_satisfaction_score || 0) * 20} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">متوسط وقت الاستجابة</span>
                    <span className="font-bold">{latestMetric?.response_time_avg_minutes || 0} دقيقة</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>مقاييس الإنتاجية</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">المهام المنجزة في الوقت</span>
                    <span className="font-bold">{latestMetric?.tasks_on_time_percentage || 0}%</span>
                  </div>
                  <Progress value={latestMetric?.tasks_on_time_percentage || 0} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">إجمالي المهام</span>
                    <span className="font-bold">{latestMetric?.tasks_completed || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>الحضور والانضباط</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">نسبة الحضور</span>
                    <span className="font-bold">{latestMetric?.attendance_percentage || 0}%</span>
                  </div>
                  <Progress value={latestMetric?.attendance_percentage || 0} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>أيام التأخير: {latestMetric?.late_days || 0}</div>
                  <div>أيام الغياب: {latestMetric?.absence_days || 0}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="goals" className="space-y-4">
          {activeGoals.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center space-y-4">
                <Target className="h-16 w-16 mx-auto text-muted-foreground" />
                <h2 className="text-xl font-bold">لا توجد أهداف نشطة</h2>
                <p className="text-muted-foreground">
                  لم يتم تعيين أي أهداف لك بعد. تواصل مع مديرك لتحديد أهداف الأداء.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
            {activeGoals.map((goal) => (
              <Card key={goal.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{goal.title}</CardTitle>
                      <CardDescription>{goal.description}</CardDescription>
                    </div>
                    <Badge>{goal.priority}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>التقدم</span>
                      <span className="font-bold">{goal.progress_percentage}%</span>
                    </div>
                    <Progress value={goal.progress_percentage} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">القيمة الحالية: </span>
                      <span className="font-bold">{goal.current_value} {goal.unit}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">الهدف: </span>
                      <span className="font-bold">{goal.target_value} {goal.unit}</span>
                    </div>
                  </div>
                  {goal.incentive_amount && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded-md">
                      <Award className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-600">
                        حافز: {goal.incentive_amount} {goal.incentive_currency}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </TabsContent>

        <TabsContent value="incentives" className="space-y-4">
          {!incentives || incentives.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center space-y-4">
                <Award className="h-16 w-16 mx-auto text-muted-foreground" />
                <h2 className="text-xl font-bold">لا توجد حوافز</h2>
                <p className="text-muted-foreground">
                  لم تحصل على أي حوافز بعد. احرص على تحقيق أهدافك لتحصل على الحوافز المستحقة.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
            {incentives?.map((incentive) => (
              <Card key={incentive.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{incentive.title}</CardTitle>
                      <CardDescription>{incentive.description}</CardDescription>
                    </div>
                    <Badge variant={
                      incentive.status === 'paid' ? 'default' :
                      incentive.status === 'approved' ? 'secondary' :
                      'outline'
                    }>
                      {incentive.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">
                      {incentive.amount} {incentive.currency}
                    </span>
                    <Badge variant="outline">{incentive.incentive_type}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}